import React from 'react'
import PropTypes from 'prop-types'
import PlaybookHeader from './PlaybookHeader'
import PlaybookMonitorView from './PlaybookMonitorView'
import PlaybookPlanView from './PlaybookPlanView'
import PlaybookRecentActivitiesView from './PlaybookRecentActivitiesView'
import {
  PLAYBOOK_STEPS_MONITOR_WRAPPER_TEST_ID,
  PLAYBOOK_STEPS_PLAN_WRAPPER_TEST_ID,
  PLAYBOOK_STEPS_RECENT_ACTIVITIES_WRAPPER_TEST_ID,
} from './constants'
import { PlaybookStepsWrapper } from './component.styles'
import { hasRecentActivitiesStartDatePassed } from './playbookHelpers'

const PlaybookSteps = ({
  activePanel,
  activeStep,
  classCreationGoalValue,
  goal1Date,
  goal2Date,
  goal3Date,
  goal3IsCompleted,
  goal3Text,
  goal4Date,
  goal4IsCompleted,
  goal4Text,
  goal5Date,
  goal5IsCompleted,
  goal5Text,
  goalsAreCompleted,
  goalsIsInDraft,
  goalsOnlyMode,
  hasAllStepsCompleted,
  implementationApproachIsCompleted,
  isModalOpen,
  isSingleSite,
  lessonCompletionGoalIsDisabled,
  lessonCompletionGoalValue,
  linkDropdownDisplayTitle,
  linkMenu,
  modalComponent,
  navigationEvent,
  numClassesMarkedLastPeriod,
  numClassesNotMarkedLesson,
  onCloseModal,
  onDropdownLinkSelect,
  onGoalSelect,
  onModalClose,
  onOpenAllStaffModal,
  onOpenFamiliesModal,
  onOpenGoalsModal,
  onOpenGoalsModalFromWarning,
  onOpenStepModal,
  onOpenSummaryModal,
  onStepClick,
  programStartDate,
  purposeIsCompleted,
  purposeIsInDraft,
  selectedGoal,
  showHeader,
  showMonitorViewOnly,
  showSummary,
  siteId,
  sitePlaybookData,
  siteUserStats,
  totalClassesCount,
}) => {
  const Modal = modalComponent
  const isPlanView = !showMonitorViewOnly
  const isMonitorView = showMonitorViewOnly
  const hasProgramStarted =
    isSingleSite &&
    hasRecentActivitiesStartDatePassed({
      programStartDate,
      lessonCompletionGoalIsDisabled,
      classCreationGoalDate: goal1Date,
    })
  const isRecentActivitiesView = isPlanView && hasProgramStarted

  const wrapperTestId = isMonitorView
    ? PLAYBOOK_STEPS_MONITOR_WRAPPER_TEST_ID
    : isRecentActivitiesView
    ? PLAYBOOK_STEPS_RECENT_ACTIVITIES_WRAPPER_TEST_ID
    : PLAYBOOK_STEPS_PLAN_WRAPPER_TEST_ID

  return (
    <PlaybookStepsWrapper
      dataTestId={wrapperTestId}
      isSingleSite={isSingleSite}
      showHeader={showHeader}
      showMonitorViewOnly={showMonitorViewOnly}
    >
      {showHeader && (
        <PlaybookHeader
          hasAllStepsCompleted={hasAllStepsCompleted}
          hasProgramStarted={hasProgramStarted}
          onOpenSummaryModal={onOpenSummaryModal}
          showMonitorViewOnly={showMonitorViewOnly}
          showSummary={showSummary}
        />
      )}
      {isPlanView && !hasProgramStarted && (
        <PlaybookPlanView
          activePanel={activePanel}
          activeStep={activeStep}
          goalsAreCompleted={goalsAreCompleted}
          goalsIsInDraft={goalsIsInDraft}
          implementationApproachIsCompleted={implementationApproachIsCompleted}
          linkDropdownDisplayTitle={linkDropdownDisplayTitle}
          linkMenu={linkMenu}
          onDropdownLinkSelect={onDropdownLinkSelect}
          onOpenAllStaffModal={onOpenAllStaffModal}
          onOpenFamiliesModal={onOpenFamiliesModal}
          onOpenStepModal={onOpenStepModal}
          onStepClick={onStepClick}
          purposeIsCompleted={purposeIsCompleted}
          purposeIsInDraft={purposeIsInDraft}
          showSummary={showSummary}
        />
      )}

      {isRecentActivitiesView && (
        <PlaybookRecentActivitiesView
          navigationEvent={navigationEvent}
          numClassesMarkedLastPeriod={numClassesMarkedLastPeriod}
          numClassesNotMarkedLesson={numClassesNotMarkedLesson}
          siteId={siteId}
          siteUserStats={siteUserStats}
          totalClassesCount={totalClassesCount}
        />
      )}

      {isMonitorView && (
        <PlaybookMonitorView
          classCreationGoalValue={classCreationGoalValue}
          goal1Date={goal1Date}
          goal2Date={goal2Date}
          goal3Date={goal3Date}
          goal3IsCompleted={goal3IsCompleted}
          goal3Text={goal3Text}
          goal4Date={goal4Date}
          goal4IsCompleted={goal4IsCompleted}
          goal4Text={goal4Text}
          goal5Date={goal5Date}
          goal5IsCompleted={goal5IsCompleted}
          goal5Text={goal5Text}
          isSingleSite={isSingleSite}
          lessonCompletionGoalIsDisabled={lessonCompletionGoalIsDisabled}
          lessonCompletionGoalValue={lessonCompletionGoalValue}
          navigationEvent={navigationEvent}
          onGoalSelect={onGoalSelect}
          onOpenGoalsModal={onOpenGoalsModal}
          onOpenGoalsModalFromWarning={onOpenGoalsModalFromWarning}
          programStartDate={programStartDate}
          selectedGoal={selectedGoal}
          showHeader={showHeader}
          showMonitorViewOnly={showMonitorViewOnly}
          siteId={siteId}
          sitePlaybookData={sitePlaybookData}
          siteUserStats={siteUserStats}
        />
      )}

      {isModalOpen && Modal && (
        <Modal
          goalsOnlyMode={goalsOnlyMode}
          handleCancel={onCloseModal}
          handleClose={goalsOnlyMode ? onCloseModal : onModalClose}
          siteId={siteId}
        />
      )}
    </PlaybookStepsWrapper>
  )
}

PlaybookSteps.propTypes = {
  activePanel: PropTypes.object,
  activeStep: PropTypes.number,
  classCreationGoalValue: PropTypes.number,
  goal1Date: PropTypes.number,
  goal2Date: PropTypes.number,
  goal3Date: PropTypes.number,
  goal3IsCompleted: PropTypes.bool,
  goal3Text: PropTypes.string,
  goal4Date: PropTypes.number,
  goal4IsCompleted: PropTypes.bool,
  goal4Text: PropTypes.string,
  goal5Date: PropTypes.number,
  goal5IsCompleted: PropTypes.bool,
  goal5Text: PropTypes.string,
  goalsAreCompleted: PropTypes.bool,
  goalsIsInDraft: PropTypes.bool,
  goalsOnlyMode: PropTypes.bool,
  hasAllStepsCompleted: PropTypes.bool,
  implementationApproachIsCompleted: PropTypes.bool,
  isModalOpen: PropTypes.bool,
  isSingleSite: PropTypes.bool,
  lessonCompletionGoalIsDisabled: PropTypes.bool,
  lessonCompletionGoalValue: PropTypes.number,
  linkDropdownDisplayTitle: PropTypes.string,
  linkMenu: PropTypes.array,
  modalComponent: PropTypes.elementType,
  navigationEvent: PropTypes.func,
  numClassesMarkedLastPeriod: PropTypes.number,
  numClassesNotMarkedLesson: PropTypes.number,
  onCloseModal: PropTypes.func,
  onDropdownLinkSelect: PropTypes.func,
  onGoalSelect: PropTypes.func,
  onModalClose: PropTypes.func,
  onOpenAllStaffModal: PropTypes.func,
  onOpenFamiliesModal: PropTypes.func,
  onOpenGoalsModal: PropTypes.func,
  onOpenGoalsModalFromWarning: PropTypes.func,
  onOpenStepModal: PropTypes.func,
  onOpenSummaryModal: PropTypes.func,
  onStepClick: PropTypes.func,
  programStartDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  purposeIsCompleted: PropTypes.bool,
  purposeIsInDraft: PropTypes.bool,
  selectedGoal: PropTypes.string,
  showHeader: PropTypes.bool,
  showMonitorViewOnly: PropTypes.bool,
  showSummary: PropTypes.bool,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sitePlaybookData: PropTypes.object,
  siteUserStats: PropTypes.shape({
    totalUsers: PropTypes.number,
    usersWithoutClasses: PropTypes.number,
    usersWithoutAccounts: PropTypes.number,
    usersLoggedInLast7Days: PropTypes.number,
  }),
  totalClassesCount: PropTypes.number,
}

export default PlaybookSteps
